import { UnreachableFunction } from 'ui/base/types';
import { Optional } from 'services/base/types';

import {
  IAccountsDomain,
  Pangolin,
  PlanFirewall,
  PlanFirewallBilling,
  PlanFirewallPackage,
  PriceFirewall,
} from 'services/accounts/types';

import { Currency, formatPrice } from 'assets/enums/currencies/currencies';

import Messages from './plan_firewall_messages';

export function getPlanFirewallPackageTier(package_type: Optional<PlanFirewallPackage>): number {
  return package_type ? {
    [PlanFirewallPackage.BASIC]: 1,
    [PlanFirewallPackage.BUSINESS]: 2,
    [PlanFirewallPackage.ENTERPRISE]: 3,
  }[package_type] : 0;
}

export function getPlanFirewallTierPackage(package_tier: number): Optional<PlanFirewallPackage> {
  return [
    undefined,
    PlanFirewallPackage.BASIC,
    PlanFirewallPackage.BUSINESS,
    PlanFirewallPackage.ENTERPRISE,
  ][package_tier];
}

export function getPlanFirewallPackageName(package_type: PlanFirewallPackage) {
  switch (package_type) {
    case PlanFirewallPackage.BASIC:
      return Messages.packageBasicName();
    case PlanFirewallPackage.BUSINESS:
      return Messages.packageBusinessName();
    case PlanFirewallPackage.ENTERPRISE:
      return Messages.packageEnterpriseName();
    default:
      return UnreachableFunction(package_type);
  }
}

export function getPlanFirewallPackageInfo(package_type: PlanFirewallPackage) {
  switch (package_type) {
    case PlanFirewallPackage.BASIC:
      return Messages.packageBasicInfo();
    case PlanFirewallPackage.BUSINESS:
      return Messages.packageBusinessInfo();
    case PlanFirewallPackage.ENTERPRISE:
      return Messages.packageEnterpriseInfo();
    default:
      return UnreachableFunction(package_type);
  }
}

export function getPlanFirewallBillingTier(billing_type: Optional<PlanFirewallBilling>): number {
  return billing_type ? {
    [PlanFirewallBilling.MONTHLY]: 1,
    [PlanFirewallBilling.YEARLY]: 2,
    [PlanFirewallBilling.BIENNIAL]: 3,
    [PlanFirewallBilling.TRIENNIAL]: 4,
    [PlanFirewallBilling.LIFETIME]: 5,
  }[billing_type] : 0;
}

export function getPlanFirewallTierBilling(billing_tier: number): Optional<PlanFirewallBilling> {
  return [
    undefined,
    PlanFirewallBilling.MONTHLY,
    PlanFirewallBilling.YEARLY,
    PlanFirewallBilling.BIENNIAL,
    PlanFirewallBilling.TRIENNIAL,
    PlanFirewallBilling.LIFETIME,
  ][billing_tier];
}

export function getPlanFirewallBillingName(billing_type: PlanFirewallBilling) {
  switch (billing_type) {
    case PlanFirewallBilling.MONTHLY:
      return Messages.billingMonthlyName();
    case PlanFirewallBilling.YEARLY:
      return Messages.billingYearlyName();
    case PlanFirewallBilling.BIENNIAL:
      return Messages.billingBiennialName();
    case PlanFirewallBilling.TRIENNIAL:
      return Messages.billingTriennialName();
    case PlanFirewallBilling.LIFETIME:
      return Messages.billingLifetimeName();
    default:
      return UnreachableFunction(billing_type);
  }
}

export function getPlanFirewallBillingInfo(
  domain: IAccountsDomain,
  billing_type: PlanFirewallBilling,
  package_type: PlanFirewallPackage
) {
  switch (billing_type) {
    case PlanFirewallBilling.MONTHLY: {
      const number_of_months_to_lifetime = Math.ceil(
        getPlanFirewallPriceAmount(domain, package_type, PlanFirewallBilling.LIFETIME)! /
        getPlanFirewallPriceAmount(domain, package_type, PlanFirewallBilling.MONTHLY)!
      );
      return Messages.billingMonthlyInfo(
        getPlanFirewallPackageName(package_type),
        number_of_months_to_lifetime,
      );
    }
    case PlanFirewallBilling.YEARLY: {
      const number_of_years_to_lifetime = Math.ceil(
        getPlanFirewallPriceAmount(domain, package_type, PlanFirewallBilling.LIFETIME)! /
        getPlanFirewallPriceAmount(domain, package_type, PlanFirewallBilling.YEARLY)!
      );
      return Messages.billingYearlyInfo(
        getPlanFirewallPackageName(package_type),
        number_of_years_to_lifetime,
      );
    }
    case PlanFirewallBilling.BIENNIAL: {
      const number_of_years_to_lifetime = Math.ceil(
        getPlanFirewallPriceAmount(domain, package_type, PlanFirewallBilling.LIFETIME)! /
        getPlanFirewallPriceAmount(domain, package_type, PlanFirewallBilling.BIENNIAL)!
      );
      return Messages.billingBiennialInfo(
        getPlanFirewallPackageName(package_type),
        number_of_years_to_lifetime,
      );
    }
    case PlanFirewallBilling.TRIENNIAL: {
      const number_of_years_to_lifetime = Math.ceil(
        getPlanFirewallPriceAmount(domain, package_type, PlanFirewallBilling.LIFETIME)! /
        getPlanFirewallPriceAmount(domain, package_type, PlanFirewallBilling.TRIENNIAL)!
      );
      return Messages.billingTriennialInfo(
        getPlanFirewallPackageName(package_type),
        number_of_years_to_lifetime,
      );
    }
    case PlanFirewallBilling.LIFETIME: {
      return Messages.billingLifetimeInfo(
        getPlanFirewallPackageName(package_type),
      );
    }
    default: {
      return UnreachableFunction(billing_type);
    }
  }
}

export function getPlanFirewallLicenseActive(plan: Optional<PlanFirewall>): boolean {
  return !!plan?.license;
}

export function getPlanFirewallSubscriptionActive(plan: Optional<PlanFirewall>): boolean {
  return !!plan?.subscription?.is_active;
}

export function getPlanFirewallPlanActive(plan: Optional<PlanFirewall>): boolean {
  return getPlanFirewallLicenseActive(plan) || getPlanFirewallSubscriptionActive(plan);
}

export function getPlanFirewallLicenseActivePackage(plan: Optional<PlanFirewall>): Optional<PlanFirewallPackage> {
  return plan?.license?.package;
}

export function getPlanFirewallSubscriptionActivePackage(plan: Optional<PlanFirewall>): Optional<PlanFirewallPackage> {
  return plan?.subscription?.is_active ? plan.subscription.package : undefined;
}

export function getPlanFirewallPlanActivePackage(plan: Optional<PlanFirewall>): Optional<PlanFirewallPackage> {
  const licensePackageTier = getPlanFirewallPackageTier(getPlanFirewallLicenseActivePackage(plan));
  const subscriptionPackageTier = getPlanFirewallPackageTier(getPlanFirewallSubscriptionActivePackage(plan));
  const activePackageTier = Math.max(licensePackageTier, subscriptionPackageTier);
  return getPlanFirewallTierPackage(activePackageTier);
}

export function authorizePlanFirewallPlan(plan: Optional<PlanFirewall>, package_id: PlanFirewallPackage): boolean {
  // If the plan is missing, return false
  if (plan === undefined) {
    return false;
  }

  // Compute the tier of the given package
  const tier = getPlanFirewallPackageTier(package_id);

  // Compare the license package tier to the given package tier
  const license = plan.license;
  if (license && getPlanFirewallPackageTier(license.package) >= tier) {
    return true;
  }

  // Compare the subscription package tier to the given package tier
  const subscription = plan.subscription;
  if (subscription?.is_active && getPlanFirewallPackageTier(subscription.package) >= tier) {
    return true;
  }

  // Return false if none of the above conditions are met
  return false;
}

export function authorizePlanFirewall(pangolin: Optional<Pangolin>, package_id: PlanFirewallPackage): boolean {
  return authorizePlanFirewallPlan(pangolin?.plans.firewall, package_id);
}

export function getPlanFirewallPriceAmount(
  domain: IAccountsDomain,
  package_type: PlanFirewallPackage,
  billing_type: PlanFirewallBilling
): Optional<number> {
  const price: Optional<PriceFirewall> = domain.prices?.firewall.find(
    (price) => (price.package === package_type &&
                price.billing === billing_type)
  );

  if (price === undefined) {
    return undefined;
  }

  return price.amount;
}

export function getPlanFirewallPriceActive(
  domain: IAccountsDomain,
  package_type: PlanFirewallPackage,
  billing_type: PlanFirewallBilling
): boolean {
  const price: Optional<PriceFirewall> = domain.prices?.firewall.find(
    (price) => (price.package === package_type &&
                price.billing === billing_type)
  );

  return price?.active ?? false;
}

export function getPlanFirewallPriceText(
  domain: IAccountsDomain,
  package_type: PlanFirewallPackage,
  billing_type: PlanFirewallBilling,
  currency: Currency,
  decimals: boolean
): Optional<string> {
  const price = getPlanFirewallPriceAmount(domain, package_type, billing_type);

  if (price === undefined) {
    return undefined;
  }

  return formatPrice(currency, price, decimals);
}

export function getPlanFirewallPricePerText(
  domain: IAccountsDomain,
  package_type: PlanFirewallPackage,
  billing_type: PlanFirewallBilling,
  currency: Currency,
  decimals: boolean
): Optional<string> {
  const price = getPlanFirewallPriceText(domain, package_type, billing_type, currency, decimals);

  if (price === undefined) {
    return undefined;
  }

  switch (billing_type) {
    case PlanFirewallBilling.MONTHLY:
      return `${price} ${Messages.billingMonthlyPer()}`;
    case PlanFirewallBilling.YEARLY:
      return `${price} ${Messages.billingYearlyPer()}`;
    case PlanFirewallBilling.BIENNIAL:
      return `${price} ${Messages.billingBiennialPer()}`;
    case PlanFirewallBilling.TRIENNIAL:
      return `${price} ${Messages.billingTriennialPer()}`;
    case PlanFirewallBilling.LIFETIME:
      return `${price} ${Messages.billingLifetimePer()}`;
    default:
      return UnreachableFunction(billing_type);
  }
}

export function getPlanFirewallEstimatedEndDate(
  billing_type: Exclude<PlanFirewallBilling, PlanFirewallBilling.LIFETIME>,
  valid_since: Date,
): Date {
  switch (billing_type) {
    case PlanFirewallBilling.MONTHLY: {
      const next_month_max = new Date(
        valid_since.getFullYear(), valid_since.getMonth() + 2, 0,
        valid_since.getHours(), valid_since.getMinutes(), valid_since.getSeconds(),
        valid_since.getMilliseconds()
      );
      const next_month = new Date(
        valid_since.getFullYear(), valid_since.getMonth() + 1, valid_since.getDate(),
        valid_since.getHours(), valid_since.getMinutes(), valid_since.getSeconds(),
        valid_since.getMilliseconds()
      );
      return next_month < next_month_max ? next_month : next_month_max;
    }
    case PlanFirewallBilling.YEARLY: {
      const next_year_max = new Date(
        valid_since.getFullYear() + 2, 0, 0,
        valid_since.getHours(), valid_since.getMinutes(), valid_since.getSeconds(),
        valid_since.getMilliseconds()
      );
      const next_year = new Date(
        valid_since.getFullYear() + 1, valid_since.getMonth(), valid_since.getDate(),
        valid_since.getHours(), valid_since.getMinutes(), valid_since.getSeconds(),
        valid_since.getMilliseconds()
      );
      return next_year < next_year_max ? next_year : next_year_max;
    }
    case PlanFirewallBilling.BIENNIAL: {
      const next_biennial_max = new Date(
        valid_since.getFullYear() + 4, 0, 0,
        valid_since.getHours(), valid_since.getMinutes(), valid_since.getSeconds(),
        valid_since.getMilliseconds()
      );
      const next_biennial = new Date(
        valid_since.getFullYear() + 2, valid_since.getMonth(), valid_since.getDate(),
        valid_since.getHours(), valid_since.getMinutes(), valid_since.getSeconds(),
        valid_since.getMilliseconds()
      );
      return next_biennial < next_biennial_max ? next_biennial : next_biennial_max;
    }
    case PlanFirewallBilling.TRIENNIAL: {
      const next_triennial_max = new Date(
        valid_since.getFullYear() + 6, 0, 0,
        valid_since.getHours(), valid_since.getMinutes(), valid_since.getSeconds(),
        valid_since.getMilliseconds()
      );
      const next_triennial = new Date(
        valid_since.getFullYear() + 3, valid_since.getMonth(), valid_since.getDate(),
        valid_since.getHours(), valid_since.getMinutes(), valid_since.getSeconds(),
        valid_since.getMilliseconds()
      );
      return next_triennial < next_triennial_max ? next_triennial : next_triennial_max;
    }
    default: {
      return UnreachableFunction(billing_type);
    }
  }
}

export const getPlanFirewallStripeItemLabel = (label: string, locale: string) => {
  const original = label;
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
  };

  let quantity = '';
  let price = '';
  let date = '';

  let regex: RegExp;
  let match: RegExpMatchArray | null;

  label = label.replace(/×/g, 'x');

  // Normalize "${quantity} × Pangolin Smart Firewall - ${plan} Plan (at $${price} / ${period})"
  regex = /^(\d+)\s*x.*- (Basic|Business|Enterprise) Plan \(at (\$\d+\.?\d*)\s*\/\s*(month|year)\)$/;
  match = label.match(regex);
  if (match) {
    quantity = match[1]; price = match[3];
    label = label.replace(regex, `%s x Pangolin Smart Firewall - ${match[2]} Plan (at %s / ${match[4]})`);
  }

  // Normalize "Unused time on Pangolin Smart Firewall - ${plan} Plan after ${date}"
  regex = /^Unused time on Pangolin Smart Firewall - (Basic|Business|Enterprise) Plan after (\d{2} \w+ \d{4})$/;
  match = label.match(regex);
  if (match) {
    date = new Date(match[2]).toLocaleDateString(locale, format);
    label = label.replace(regex, `Unused time on Pangolin Smart Firewall - ${match[1]} Plan after %s`);
  }

  switch (label) {
    case 'Trial period for Pangolin Smart Firewall - Basic Plan':
      return Messages.stripeItemTrialBasic();
    case 'Trial period for Pangolin Smart Firewall - Business Plan':
      return Messages.stripeItemTrialBusiness();
    case 'Trial period for Pangolin Smart Firewall - Enterprise Plan':
      return Messages.stripeItemTrialEnterprise();
    case 'Pangolin Smart Firewall - Basic Plan':
      return Messages.stripeItemLifetimeBasic();
    case 'Pangolin Smart Firewall - Business Plan':
      return Messages.stripeItemLifetimeBusiness();
    case 'Pangolin Smart Firewall - Enterprise Plan':
      return Messages.stripeItemLifetimeEnterprise();
    case '%s x Pangolin Smart Firewall - Basic Plan (at %s / month)':
      return Messages.stripeItemMonthlyBasic(quantity, price);
    case '%s x Pangolin Smart Firewall - Business Plan (at %s / month)':
      return Messages.stripeItemMonthlyBusiness(quantity, price);
    case '%s x Pangolin Smart Firewall - Enterprise Plan (at %s / month)':
      return Messages.stripeItemMonthlyEnterprise(quantity, price);
    case '%s x Pangolin Smart Firewall - Basic Plan (at %s / year)':
      return Messages.stripeItemYearlyBasic(quantity, price);
    case '%s x Pangolin Smart Firewall - Business Plan (at %s / year)':
      return Messages.stripeItemYearlyBusiness(quantity, price);
    case '%s x Pangolin Smart Firewall - Enterprise Plan (at %s / year)':
      return Messages.stripeItemYearlyEnterprise(quantity, price);
    case '%s x Pangolin Smart Firewall - Basic Plan (at %s / 2 years)':
      return Messages.stripeItemBiennialBasic(quantity, price);
    case '%s x Pangolin Smart Firewall - Business Plan (at %s / 2 years)':
      return Messages.stripeItemBiennialBusiness(quantity, price);
    case '%s x Pangolin Smart Firewall - Enterprise Plan (at %s / 2 years)':
      return Messages.stripeItemBiennialEnterprise(quantity, price);
    case '%s x Pangolin Smart Firewall - Basic Plan (at %s / 3 years)':
      return Messages.stripeItemTriennialBasic(quantity, price);
    case '%s x Pangolin Smart Firewall - Business Plan (at %s / 3 years)':
      return Messages.stripeItemTriennialBusiness(quantity, price);
    case '%s x Pangolin Smart Firewall - Enterprise Plan (at %s / 3 years)':
      return Messages.stripeItemTriennialEnterprise(quantity, price);
    case 'Unused time on Pangolin Smart Firewall - Basic Plan after %s':
      return Messages.stripeItemUnusedTimeBasic(date);
    case 'Unused time on Pangolin Smart Firewall - Business Plan after %s':
      return Messages.stripeItemUnusedTimeBusiness(date);
    case 'Unused time on Pangolin Smart Firewall - Enterprise Plan after %s':
      return Messages.stripeItemUnusedTimeEnterprise(date);
    case 'Applied from Stripe Balance':
      return Messages.stripeItemStripeBalance();
    default:
      return original;
  }
};
